import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, CartesianGrid, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { Calendar, Clock, BookOpen, Users, Award, FileText, CheckSquare, Bell } from 'lucide-react';
import "../../css/teacher/Dashboard.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../components/Loader";

function Dashboard() {
    const [classes, setClasses] = useState([]);
    const [todaysClasses, setTodaysClasses] = useState([]);
    const [students, setStudents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [performanceData, setPerformanceData] = useState([]);
    const [announcements, setAnnouncements] = useState([]);
    const [teacherName, setTeacherName] = useState("");
    const [attendanceStats, setAttendanceStats] = useState({
        today: 'not marked',
        monthly: { present: 0, workingDays: 0, percentage: '0.0' },
        yearly: { present: 0, workingDays: 0, percentage: '0.0' }
    });

    const COLORS = {
        present: '#4caf50',
        absent: '#f44336',
        late: '#ff9800',
        average: '#3498db'
    };


    const [upcomingAssignments, setUpcomingAssignments] = useState([]);
    // Attendance data for pie chart
    const [attendanceData, setAttendanceData] = useState([
        { name: 'Present', value: 0, color: '#4caf50' },
        { name: 'Absent', value: 0, color: '#f44336' },
        { name: 'Late', value: 0, color: '#ff9800' },
    ]);

    // Current date formatted
    const currentDate = new Date().toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    });

    // Get current day of week (0 = Sunday, 1 = Monday, etc.)
    const currentDayOfWeek = new Date().getDay();
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const today = daysOfWeek[currentDayOfWeek];




    useEffect(() => {
        fetchClasses();
        fetchTodaysClasses();
        fetchStudents();
        fetchAttendanceStats();
        fetchAnnouncements();
        fetchExams();
        fetchTeacherName();
    }, []);


    // Add fetchTeacherName function
const fetchTeacherName = async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Unauthorized! Please log in again.");
      return;
    }
  
    try {
      const response = await fetch("https://schoolpro-api.uniums.com/api/student/profile/name", {
        headers: { "Authorization": `Bearer ${token}` }
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch student name");
      }
  
      const data = await response.json();
      setTeacherName(data.name);
    } catch (error) {
      console.error("Error fetching student name:", error);
      toast.error(`Error: ${error.message}`);
    }
  };

    // Fetch classes data
    const fetchClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/classes", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch classes");
            }

            const data = await response.json();

            // 🔁 Map API structure to frontend structure
            const mappedClasses = data.map((item) => ({
                id: item._id,
                name: `${item.name} - Section ${item.section}`,
                time: item.time || "N/A", // Replace with real data if available
                room: item.room || "N/A", // Replace with real data if available
            }));

            setClasses(mappedClasses);
        } catch (error) {
            console.error("Error fetching classes:", error);
            toast.error(`Error: ${error.message}`);
            setClasses([]); // or fallback data
        } finally {
            setIsLoading(false);
        }
    };

    // Fetch today's classes from routines
    const fetchTodaysClasses = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/routine", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch routines");
            }

            const routines = await response.json();

            // Normalize the current day to match API keys (e.g., "Monday" -> "monday")
            const todayKey = today.toLowerCase();

            // Extract classes for today from the schedule object
            const classesForToday = routines
                .flatMap(routine => {
                    // Check if schedule and today's key exist
                    if (!routine.schedule || !Array.isArray(routine.schedule[todayKey])) {
                        return [];
                    }

                    // Map the classes for today
                    return routine.schedule[todayKey].map(classItem => ({
                        id: classItem._id || `${routine._id}-${classItem.startTime}`, // Generate a unique ID if _id is missing
                        name: routine.classId
                            ? `${routine.classId.name} - Section ${routine.classId.section}`
                            : "Class not found",
                        time: classItem.startTime || "N/A",
                        room: classItem.room || routine.classId?.room || "N/A",
                        subject: classItem.subject || routine.classId?.subject || "N/A"
                    }));
                })
                .sort((a, b) => {
                    // Sort by time if available
                    if (a.time && b.time) {
                        return a.time.localeCompare(b.time);
                    }
                    return 0;
                });

            setTodaysClasses(classesForToday);
        } catch (error) {
            console.error("Error fetching today's classes:", error);
            toast.error(`Error: ${error.message}`);
            setTodaysClasses([]); // Set empty array as fallback
        } finally {
            setIsLoading(false);
        }
    };

    const fetchStudents = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/students", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch students");
            }

            const data = await response.json();
            setStudents(data);
        } catch (error) {
            console.error("Error fetching students:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };


    // Fetch attendance statistics for the teacher
    const fetchAttendanceStats = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/attendance/stats", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch attendance statistics");
            }

            const { data } = await response.json();
            setAttendanceStats(data);

            // Update performance data based on attendance
            setPerformanceData([
                { name: "Monthly", average: parseFloat(data.monthly.percentage) },
                { name: "Yearly", average: parseFloat(data.yearly.percentage) }
            ]);

            // Update attendanceData for PieChart (assuming API provides overall counts)
            setAttendanceData([
                { name: 'Present', value: data.overall?.presentCount || data.monthly.present || 0, color: '#4caf50' },
                { name: 'Absent', value: data.overall?.absentCount || (data.monthly.workingDays - data.monthly.present) || 0, color: '#f44336' },
                { name: 'Late', value: data.overall?.lateCount || 0, color: '#ff9800' }
            ]);

        } catch (error) {
            console.error("Error fetching attendance statistics:", error);
            toast.error(`Error: ${error.message}`);
            // Fallback data for performance data
            setPerformanceData([
                { name: "Monthly", average: 92 },
                { name: "Yearly", average: 95 }
            ]);
            // Fallback data for attendanceData
            setAttendanceData([
                { name: 'Present', value: 80, color: '#4caf50' },
                { name: 'Absent', value: 15, color: '#f44336' },
                { name: 'Late', value: 5, color: '#ff9800' }
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    // Check if attendance data is empty
    const hasAttendanceData = attendanceData.some(item => item.value > 0);

    // Create formatted attendance status message
    const getAttendanceStatusText = () => {
        const { today } = attendanceStats;

        if (today === 'present') {
            return "Present Today";
        } else if (today === 'absent') {
            return "Absent Today";
        } else if (today === 'late') {
            return "Late Today";
        } else {
            return "Not Marked Yet";
        }
    };

    // Create formatted attendance percentage display
    const getAttendancePercentage = () => {
        return attendanceStats.monthly.percentage + '%';
    };


    // Fetch announcements
    const fetchAnnouncements = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/announcement", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch announcements");
            }

            const data = await response.json();
            // Map API structure to frontend structure
            const mappedAnnouncements = data.map((item) => ({
                id: item._id,
                title: item.title,
                date: new Date(item.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                }),
                priority: item.priority || "Medium"
            }));

            setAnnouncements(mappedAnnouncements);
        } catch (error) {
            console.error("Error fetching announcements:", error);
            toast.error(`Error: ${error.message}`);
            setAnnouncements([]); // or fallback data
        } finally {
            setIsLoading(false);
        }
    };

    // Add fetchExams function
    const fetchExams = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/teacher/exams", {
                headers: { "Authorization": `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch exams");
            }

            const data = await response.json();
            // Map API structure to frontend structure
            const mappedExams = data.map((exam) => ({
                id: exam._id,
                examName: exam.examName,
                examType: exam.examType,
                dueDate: new Date(exam.date).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                }),
                class: exam.class && exam.classId ? `${exam.classId.name} - Section ${exam.section}` : exam.section || "N/A"
            }));

            setUpcomingAssignments(mappedExams);
        } catch (error) {
            console.error("Error fetching exams:", error);
            toast.error(`Error: ${error.message}`);
            setUpcomingAssignments([]);
        } finally {
            setIsLoading(false);
        }
    };


    return (
        <div className="teacher-dashboard-container">
            {isLoading && <Loader />}
            <ToastContainer position="top-right" autoClose={3000} />

            {/* Header Section */}
            <div className="teacher-dashboard-header">
                <div className="teacher-dashboard-welcome">
                    <h1>Welcome back, {teacherName}</h1>
                    <p className="teacher-dashboard-date">
                        <Calendar size={16} className="teacher-dashboard-icon" />
                        {currentDate}
                    </p>
                </div>
                <div className="teacher-dashboard-stats">
                    <div className="teacher-dashboard-stat-card">
                        <Users size={20} className="teacher-dashboard-stat-icon" />
                        <div>
                            <p className="teacher-dashboard-stat-value">{students.length || 0}</p>
                            <p className="teacher-dashboard-stat-label">Students</p>
                        </div>
                    </div>
                    <div className="teacher-dashboard-stat-card">
                        <BookOpen size={20} className="teacher-dashboard-stat-icon" />
                        <div>
                            <p className="teacher-dashboard-stat-value">{classes.length || 0}</p>
                            <p className="teacher-dashboard-stat-label">Classes</p>
                        </div>
                    </div>
                    <div className="teacher-dashboard-stat-card">
                        <Award size={20} className="teacher-dashboard-stat-icon" />
                        <div>
                            <p className="teacher-dashboard-stat-value">{getAttendancePercentage()}</p>
                            <p className="teacher-dashboard-stat-label">Attendance</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Grid Layout */}
            <div className="teacher-dashboard-grid">
                {/* Today's Classes Card */}
                <div className="teacher-dashboard-card teacher-dashboard-classes">
                    <div className="teacher-dashboard-card-header">
                        <h2><Clock size={18} className="teacher-dashboard-icon" /> Today's Classes ({today})</h2>
                    </div>
                    <div className="teacher-dashboard-card-body">
                        {todaysClasses.length > 0 ? (
                            todaysClasses.map(classItem => (
                                <div key={classItem.id} className="teacher-dashboard-class-item">
                                    <div className="teacher-dashboard-class-time">{classItem.time}</div>
                                    <div className="teacher-dashboard-class-details">
                                        <h3>{classItem.name}</h3>
                                        <p>Subject: {classItem.subject} • Room {classItem.room}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="teacher-dashboard-no-data">
                                <p>No classes scheduled for today ({today})</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Attendance Overview Card */}
                <div className="teacher-dashboard-card teacher-dashboard-attendance">
                    <div className="teacher-dashboard-card-header">
                        <h2><Users size={18} className="teacher-dashboard-icon" /> Class Attendance</h2>
                    </div>
                    <div className="teacher-dashboard-card-body">
                        {hasAttendanceData ? (
                            <ResponsiveContainer width="100%" height={200}>
                                <PieChart>
                                    <Pie
                                        data={attendanceData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={80}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {attendanceData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={entry.color} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => [`${value} students`, null]} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="teacher-dashboard-no-data">
                                <p>No attendance data available</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Teacher Attendance Card */}
                <div className="teacher-dashboard-card teacher-dashboard-performance">
                    <div className="teacher-dashboard-card-header">
                        <h2><Award size={18} className="teacher-dashboard-icon" /> My Attendance</h2>
                    </div>
                    <div className="teacher-dashboard-card-body">
                        <div className="teacher-dashboard-attendance-stats">
                            <div className="teacher-dashboard-attendance-today">
                                <h3>Status: {getAttendanceStatusText()}</h3>
                            </div>
                        </div>
                        <ResponsiveContainer width="100%" height={180}>
                            <BarChart
                                data={[
                                    { name: "Monthly", percentage: parseFloat(attendanceStats.monthly.percentage) },
                                    { name: "Yearly", percentage: parseFloat(attendanceStats.yearly.percentage) }
                                ]}
                                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 100]} />
                                <Tooltip formatter={(value) => [`${value}%`, "Attendance"]} />
                                <Bar dataKey="percentage" name="Attendance Rate" fill={COLORS.present} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                {/* Assignments Card */}
                <div className="teacher-dashboard-card teacher-dashboard-assignments">
                    <div className="teacher-dashboard-card-header">
                        <h2><FileText size={18} className="teacher-dashboard-icon" /> Upcoming Exams</h2>
                    </div>
                    <div className="teacher-dashboard-card-body">
                        {upcomingAssignments.length > 0 ? (
                            upcomingAssignments.map(assignment => (
                                <div key={assignment.id} className="teacher-dashboard-assignment-item">
                                    <div className="teacher-dashboard-assignment-content">
                                        <h3>{assignment.examName}</h3>
                                        <p>Type: {assignment.examType} • {assignment.class} • Due: {assignment.dueDate}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="teacher-dashboard-no-data">
                                <p>No upcoming exams available</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Announcements Card */}
                <div className="teacher-dashboard-card teacher-dashboard-announcements">
                    <div className="teacher-dashboard-card-header">
                        <h2><Bell size={18} className="teacher-dashboard-icon" /> Announcements</h2>
                    </div>
                    <div className="teacher-dashboard-card-body">
                        {announcements.map(announcement => (
                            <div key={announcement.id} className="teacher-dashboard-announcement-item">
                                <div className="teacher-dashboard-announcement-content">
                                    <h3>{announcement.title}</h3>
                                    <p>{announcement.date}</p>
                                </div>
                                <span className={`teacher-dashboard-priority teacher-dashboard-priority-${announcement.priority.toLowerCase()}`}>
                                    {announcement.priority}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Dashboard;